import React, { useState, useEffect } from 'react';
import { Form, Modal, theme } from 'antd';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { t } from 'i18next';
import { EPButton } from '@/components';
import { AdditionalInformationModalContainer } from './AdditionalInformationModal.styles';
import { useMeasurement } from '@/hooks/useMeasurement';
import { AdditionalInformationModalProps, ModalMode, INFORMATION_MODES } from './AdditionalInformationModal.utils';
import SpecModeForm from './SpecModeForm';
import ManualModeForm from './ManualModeForm';

const { useToken } = theme;

const AdditionalInformationModal: React.FC<AdditionalInformationModalProps> = ({
  open,
  onClose,
  onSave,
  title,
  fieldNamePath,
  initialMode = INFORMATION_MODES.SPEC,
}) => {
  const { token } = useToken();
  const { tunit } = useMeasurement();
  const form = Form.useFormInstance();
  const [mode, setMode] = useState<ModalMode>(initialMode);
  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const selectedPlatform = Form.useWatch([...fieldNamePath, 'platform'], form);

  const handlePressureChange = (value: string, fieldPath: (string | number)[]) => {
    const numericValue = parseFloat(value);
    const pressureUnit = tunit('bar');

    if (!isNaN(numericValue) && pressureUnit === 'bar' && numericValue > 12) {
      modal.confirm({
        icon: <ExclamationCircleOutlined />,
        content: t('Units are in bar, do you want to continue?'),
        okText: t('Yes'),
        cancelText: t('Cancel'),
        onOk() {
          form.setFieldValue(fieldPath, value);
        },
        onCancel() {
          form.setFieldValue(fieldPath, '');
        },
      });
    } else {
      form.setFieldValue(fieldPath, value);
    }
  };

  const handleSave = () => {
    onSave();
    onClose();
  };

  const handleModeSwitch = (newMode: ModalMode) => {
    if (mode === INFORMATION_MODES.SPEC) {
      form.setFieldValue([...fieldNamePath, 'specUrl'], undefined);
    } else {
      form.setFieldValue([...fieldNamePath, 'driveType'], undefined);
      form.setFieldValue([...fieldNamePath, 'cooling'], undefined);
      form.setFieldValue([...fieldNamePath, 'horsepower'], undefined);
    }
    setMode(newMode);
  };

  const handlePlatformChange = () => {
    form.setFieldValue([...fieldNamePath, 'driveType'], undefined);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={460}
      footer={[
        <EPButton key="save" type="primary" size="large" onClick={handleSave}>
          {t(mode === INFORMATION_MODES.SPEC ? 'Save data' : 'Save')}
        </EPButton>,
      ]}
      centered
      maskClosable={false}
      closeIcon={<CloseOutlined />}
    >
      <AdditionalInformationModalContainer theme={{ token }}>
        <div className="modal-header">
          <h3 className="modal-title">
            {t('Add information for setup of')} {title}
          </h3>
        </div>

        <div className="modal-content">
          {mode === INFORMATION_MODES.SPEC ? (
            <SpecModeForm fieldNamePath={fieldNamePath} onPressureChange={handlePressureChange} />
          ) : (
            <ManualModeForm
              fieldNamePath={fieldNamePath}
              selectedPlatform={selectedPlatform}
              onPressureChange={handlePressureChange}
              onPlatformChange={handlePlatformChange}
            />
          )}

          <div className="divider-section">
            <div className="divider-line"></div>
            <span>{mode === INFORMATION_MODES.SPEC ? t("Don't have a URL?") : t('Have a spec URL instead?')}</span>
            <div className="divider-line"></div>
          </div>

          <div className="manual-entry-section">
            <EPButton
              type="link"
              size="small"
              onClick={() =>
                handleModeSwitch(mode === INFORMATION_MODES.SPEC ? INFORMATION_MODES.MANUAL : INFORMATION_MODES.SPEC)
              }
            >
              {t(mode === INFORMATION_MODES.SPEC ? 'Enter details manually' : 'Add spec link instead')}
            </EPButton>
          </div>
        </div>
      </AdditionalInformationModalContainer>
      {contextHolder}
    </Modal>
  );
};

export default AdditionalInformationModal;
